import React from "react";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { ChevronRight } from "lucide-react";

const FAQS = [
  {
    q: "What is WriterSpark?",
    a: "WriterSpark is an interactive AI writing coach for students. It supports essay writing, argumentative writing, narrative writing, constructed responses, and writing revision with rubric-aligned, grade-adapted feedback.",
  },
  {
    q: "Will WriterSpark write essays for my child?",
    a: "No. WriterSpark coaches — it does not write the student's draft. The platform asks Socratic questions, models thinking, scores against rubrics, and gives specific revision suggestions, but the writing stays the student's own work.",
  },
  {
    q: "What grade levels does WriterSpark support?",
    a: "Elementary, middle, and high school. Coaching tone and rubric vocabulary automatically adapts to the selected grade band.",
  },
  {
    q: "Does WriterSpark support SAT and AP writing prep?",
    a: "Yes. SAT/AP Prep mode runs timed essays scored on the College Board's argument-essay rubric with targeted revision suggestions.",
  },
  {
    q: "How is student writing data handled?",
    a: "Student writing is processed for AI inference only and is not used to train any underlying AI model. See our privacy policy for full details.",
  },
  {
    q: "Can parents and teachers see student progress?",
    a: "Yes. Parents and teachers can link to students via invite code or email. Mentor dashboards show streaks, badges, rubric trends, and recent sessions per student.",
  },
  {
    q: "Is there a free plan?",
    a: "Yes. The free plan includes 5 AI coaching sessions per day, daily prompts, basic grammar drills, and up to 3 revision dimensions per draft.",
  },
  {
    q: "What's included in Premium?",
    a: "Unlimited coaching sessions, advanced Revision Intelligence (up to 6 dimensions), SAT/AP timed essay scoring, the Interactive Writing Conference, and full growth analytics.",
  },
  {
    q: "Does WriterSpark work on mobile?",
    a: "Yes. WriterSpark is a Progressive Web App — it works in any modern browser on phone, tablet, or desktop, and can be installed as an app from the browser's 'Add to Home Screen' menu.",
  },
  {
    q: "How can my school use WriterSpark?",
    a: "Use the School & Educator plan — classroom rosters, per-student analytics, and class-level insights. Contact us through the Contact page to set up a trial.",
  },
];

export default function Faq() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <Seo path="/faq" schema={schema} />
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>
          Frequently asked questions
        </h1>
        <p className="text-slate-600 mb-10">
          Everything you need to know about WriterSpark — the AI writing coach for students.
        </p>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} className="tactile-card p-5 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-bold text-slate-900 flex items-center justify-between gap-4">
                <span>{f.q}</span>
                <ChevronRight size={16} className="flex-shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
